<template>
  <div
    v-if="show"
    class="modal d-block"
    style="background-color: rgba(0, 0, 0, 0.5);"
    tabindex="-1"
    @mousedown.self="onCancel"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content shadow">

        <div class="modal-header py-2">
          <h6 class="modal-title mb-0">
            <i class="fas fa-image me-2 text-muted"></i>Insert / edit image
          </h6>
          <button type="button" class="btn-close" @click="onCancel"></button>
        </div>

        <div class="modal-body">

          <div class="mb-3">
            <label class="form-label small fw-semibold">Image URL</label>
            <div class="d-flex gap-2 align-items-start">
              <input
                v-model="form.src"
                type="text"
                class="form-control form-control-sm"
                placeholder="https://..."
              />
              <badiu-tiptap-image-upload
                :config="config"
                @uploaded="onUploaded"
                @error="onUploadError"
              />
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label small fw-semibold">Description (alt)</label>
            <input
              v-model="form.alt"
              type="text"
              class="form-control form-control-sm"
              placeholder="Alternative text"
            />
          </div>

          <div class="row g-3 mb-3">
            <div class="col">
              <label class="form-label small fw-semibold">Width</label>
              <input
                v-model="form.width"
                type="text"
                class="form-control form-control-sm"
                placeholder="auto"
              />
            </div>
            <div class="col">
              <label class="form-label small fw-semibold">Height</label>
              <input
                v-model="form.height"
                type="text"
                class="form-control form-control-sm"
                placeholder="auto"
              />
            </div>
          </div>

          <div
            v-if="form.src"
            class="text-center p-3 rounded border bg-light"
          >
            <p class="mb-2 small text-muted">Preview</p>
            <img
              :src="form.src"
              :alt="form.alt || ''"
              class="img-fluid rounded"
              style="max-height: 180px;"
            />
          </div>

        </div>

        <div class="modal-footer py-2">
          <button
            type="button"
            class="btn btn-secondary btn-sm"
            @click="onCancel"
          >
            Cancel
          </button>

          <button
            type="button"
            class="btn btn-primary btn-sm"
            :disabled="!form.src"
            @click="onSave"
          >
            Save
          </button>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
/**
 * BadiuTiptapImage
 *
 * Isolated modal component for inserting and editing images.
 * Does NOT handle upload directly — delegates to BadiuTiptapImageUpload.
 */
import { reactive, watch } from 'vue'
import BadiuTiptapImageUpload from './BadiuTiptapImageUpload.vue'

export default {
  name: "BadiuTiptapImage",

  components: {
    BadiuTiptapImageUpload
  },

  props: {
    show: {
      type: Boolean,
      default: false
    },

    initial: {
      type: Object,
      default: () => ({})
    },

    /** Forwarded to BadiuTiptapImageUpload (ver BadiuTiptapEditor para doc das chaves). */
    config: {
      type: Object,
      default: () => ({})
    }
  },

  emits: ["save", "cancel", "uploaded", "error"],

  setup(props, { emit }) {
    const form = reactive({
      src: "",
      alt: "",
      width: "",
      height: ""
    });

    function loadImageDimensions(src) {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload  = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
        img.onerror = () => resolve(null);
        img.src = src;
      });
    }

    watch(
      () => props.show,
      (visible) => {
        if (visible) {
          form.src    = props.initial.src    || "";
          form.alt    = props.initial.alt    || "";
          form.width  = props.initial.width  || "";
          form.height = props.initial.height || "";
        }
      },
      { immediate: true }
    );

    watch(
      () => form.src,
      async (src) => {
        if (!src || form.width || form.height) return;
        const dims = await loadImageDimensions(src);
        if (dims && !form.width && !form.height) {
          form.width  = String(dims.width);
          form.height = String(dims.height);
        }
      }
    );

    function onSave() {
      if (!form.src) return;

      const data = { src: form.src, alt: form.alt || null };

      if (form.width)  data.width  = form.width;
      if (form.height) data.height = form.height;

      emit("save", data);
    }

    function onCancel() {
      emit("cancel");
    }

    async function onUploaded(payload) {
      form.src = payload.src;

      if (!form.alt && payload.name) {
        form.alt = payload.name;
      }

      const dims = await loadImageDimensions(payload.src);
      if (dims) {
        form.width  = String(dims.width);
        form.height = String(dims.height);
      }

      emit("uploaded", payload);
    }

    function onUploadError(payload) {
      emit("error", payload);
    }

    return {
      form,
      onSave,
      onCancel,
      onUploaded,
      onUploadError
    };
  }
};
</script>
